import React, { useState, useRef, useEffect } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

const FACILITY_TYPES = [
  { value: 'elderly',    label: 'RCFE',  description: 'Residential Care Facility for the Elderly' },
  { value: 'group',      label: 'GH',    description: 'Group Home' },
  { value: 'adult',      label: 'ARF',   description: 'Adult Residential Facility' },
  { value: 'short_term', label: 'STRTP', description: 'Short-Term Residential Therapeutic Program' },
];

const inputClass =
  "block w-full rounded-lg border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder:text-gray-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 focus:outline-none transition";

function Field({ id, label, optional, children }) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-semibold text-gray-700 mb-1.5">
        {label}
        {optional && <span className="ml-1.5 text-xs font-normal text-gray-400">(optional)</span>}
      </label>
      {children}
    </div>
  );
}

function SectionCard({ icon, title, description, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center shrink-0">
          {icon}
        </div>
        <div className="min-w-0">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function StateMultiSelect({ selected, onChange }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = US_STATES.filter((s) => !selected.includes(s) && s.toLowerCase().includes(search.toLowerCase()));

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={() => setOpen(true)}
        placeholder="Search states…"
        className={inputClass}
      />
      {open && filtered.length > 0 && (
        <ul className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/5">
          {filtered.map((state) => (
            <li
              key={state}
              onClick={() => { onChange([...selected, state]); setSearch(''); }}
              className="cursor-pointer px-3.5 py-2 text-sm text-gray-900 hover:bg-indigo-50"
            >
              {state}
            </li>
          ))}
        </ul>
      )}
      {selected.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {selected.map((state) => (
            <span
              key={state}
              className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-700 border border-indigo-200"
            >
              {state}
              <button
                type="button"
                onClick={() => onChange(selected.filter((s) => s !== state))}
                className="text-indigo-400 hover:text-indigo-700 cursor-pointer"
              >
                <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function ConversionNotice() {
  return (
    <section
      aria-labelledby="conversion-notice-heading"
      className="rounded-2xl border border-indigo-200 bg-indigo-50 p-5"
    >
      <h2 id="conversion-notice-heading" className="text-sm font-semibold text-indigo-900">
        What happens when you create this facility
      </h2>
      <ul className="mt-2 space-y-1.5 text-sm text-indigo-900/90 list-disc list-inside">
        <li>You become the owner of this facility and can invite and manage team members.</li>
        <li>Your account gains facility management tools alongside your own training.</li>
        <li>
          Your training history, certificates, purchases, and sign-in details stay exactly as they
          are — nothing is reset and no new account is created.
        </li>
      </ul>
    </section>
  );
}

export default function New({ converting = false }) {
  const [name, setName] = useState('');
  const [facilityType, setFacilityType] = useState('');
  const [selectedStates, setSelectedStates] = useState([]);
  const [licenseNumber, setLicenseNumber] = useState('');
  const [errors, setErrors] = useState([]);
  const [invalidFields, setInvalidFields] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const errorSummaryRef = useRef(null);

  // The form carries noValidate and no `required` attributes: the browser's own
  // constraint bubbles would block submit before this component ever ran, so
  // the summary below would never appear. Validation is this component's job,
  // and the summary is the single accessible announcement of it.
  useEffect(() => {
    if (errors.length > 0) errorSummaryRef.current?.focus();
  }, [errors]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrors([]);
    setInvalidFields([]);

    const validationErrors = [];
    const missing = [];
    if (!name.trim()) {
      validationErrors.push('Facility name is required');
      missing.push('name');
    }
    if (!facilityType) {
      validationErrors.push('Facility type is required');
      missing.push('facility_type');
    }

    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setInvalidFields(missing);
      return;
    }

    setSubmitting(true);

    // The conversion form posts to its own endpoint so the server can refuse a
    // duplicate or stale submit instead of treating it as an extra facility.
    router.post(converting ? '/facilities/convert' : '/facilities', {
      facility: {
        name: name.trim(),
        facility_type: facilityType,
        license_number: licenseNumber.trim() || null,
      },
      facility_states: selectedStates,
    }, {
      onError: () => {
        setErrors(['Failed to create facility. Please check all fields.']);
        setSubmitting(false);
      },
      onFinish: () => setSubmitting(false),
    });
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <Link href="/" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-1.5">
            <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to dashboard
          </Link>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            {converting ? 'Set Up Your Facility' : 'Add New Facility'}
          </h1>
          <p className="mt-1 text-sm text-gray-500">Tell us about your facility so we can tailor compliance and training.</p>
        </div>

        {converting && <ConversionNotice />}

        {errors.length > 0 && (
          <div
            id="facility-form-errors"
            role="alert"
            tabIndex={-1}
            ref={errorSummaryRef}
            className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800 flex items-start gap-3 focus:outline-none"
          >
            <div className="w-8 h-8 bg-white text-rose-600 rounded-lg flex items-center justify-center shrink-0">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold">Please fix the following:</p>
              <ul className="mt-1 list-disc list-inside space-y-0.5 text-rose-700">
                {errors.map((error, i) => (
                  <li key={i}>{error}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <SectionCard
            title="Basic Information"
            description="How your facility appears across the portal."
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            }
          >
            <Field id="name" label="Facility name">
              <input
                type="text"
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                aria-invalid={invalidFields.includes('name')}
                aria-describedby={invalidFields.includes('name') ? 'facility-form-errors' : undefined}
                placeholder="Enter facility business name"
                className={inputClass}
              />
            </Field>
          </SectionCard>

          <SectionCard
            title="Facility Type"
            description="The regulatory category that applies to your facility."
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            }
          >
            <fieldset
              role="radiogroup"
              className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              aria-invalid={invalidFields.includes('facility_type')}
              aria-describedby={invalidFields.includes('facility_type') ? 'facility-form-errors' : undefined}
            >
              <legend className="sr-only">Facility type</legend>
              {FACILITY_TYPES.map((type) => {
                const selected = facilityType === type.value;
                return (
                  <label
                    key={type.value}
                    className={`flex items-start gap-3 cursor-pointer p-4 border-2 rounded-xl transition-colors ${
                      selected ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name="facility_type"
                      value={type.value}
                      checked={selected}
                      onChange={() => setFacilityType(type.value)}
                      className="mt-1 h-4 w-4 text-indigo-600"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900">{type.label}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{type.description}</p>
                    </div>
                  </label>
                );
              })}
            </fieldset>
          </SectionCard>

          <SectionCard
            title="Location &amp; Licensing"
            description="States your facility operates in, and your state-issued license number."
            icon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            }
          >
            <div className="space-y-5">
              <Field id="states" label="Facility state(s)">
                <StateMultiSelect selected={selectedStates} onChange={setSelectedStates} />
              </Field>
              <Field id="license_number" label="License number" optional>
                <input
                  type="text"
                  id="license_number"
                  value={licenseNumber}
                  onChange={(e) => setLicenseNumber(e.target.value)}
                  placeholder="State-issued license number"
                  className={inputClass}
                />
              </Field>
            </div>
          </SectionCard>

          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <Link
              href="/"
              className="flex-1 inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition cursor-pointer"
            >
              {submitting ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                  Creating…
                </>
              ) : 'Create Facility'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
